import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React, { useState } from 'react';
import { TFunction } from 'react-i18next';
import { usePrefixTranslation } from '../../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../../redux';
import { theme } from '../../../../theme/theme';
import { Asset } from '../../../../types/asset';
import { Plan } from '../../../../types/plan';
import { getContentUrl } from '../../../../util/file';
import { DisplayPicture, PictureUploader } from '../../../global/PictureUploader';
import { Property } from '../../../global/Property';
import { SectionState } from '../InspectionCreationWizard';

const mod = 'sections.ImageDesign';

/**
 * Number of internal steps this section contains.
 */
export const steps = 1;
export const label = `${mod}.summary-label`;

/**
 * Header generator.
 *
 * @param t translation function
 * @param state the current section state
 * @param focused if is the current section
 * @param asset selected asset
 * @param plan asset plan
 * @returns header title and subtitle
 */
export const header = (t: TFunction<string[]>, state: SectionState, focused: boolean, asset?: Asset, plan?: Plan) => ({
    title: t(`${mod}.header-title${!focused ? '-small' : ''}`),
    subtitle: t(`${mod}.header-subtitle${!focused ? '-small' : ''}`, { count: 5 }),
});

const useComponentClasses = makeStyles({
    root: {
        display: 'flex',
        flex: 1,
        margin: '0 -10px',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        margin: '0 10px',
    },
    title: {
        color: theme.colors.neutral[1],
        ...theme.typography.headings.h5,
    },
    subtitle: {
        color: theme.colors.neutral[1],
        ...theme.typography.content.caption.paragraph,
        marginBottom: 20,
        '& span': {
            color: theme.colors.blue[1],
            cursor: 'pointer',
        },
    },
    items: {
        '&.editable > div': {
            margin: '12px 0',
        },
    },
});

/**
 * Image design section of the inspection creation process.
 *
 * @param props.state the current section state
 * @param props.next function to advance to the next step
 */
export const Component = (props: { state: SectionState; next: () => void }) => {
    const classes = useComponentClasses();
    const { t, Trans } = usePrefixTranslation('inspection-creation', { 'inspection-creation': 'sections.ImageDesign' });
    const dispatch = useTypedDispatch();
    const { asset, version } = useTypedSelector(state => ({
        asset: state.asset.changed,
        version: state.asset.changes.length,
    }));
    const uploadRef = React.useRef<() => Promise<void>>();
    const designMetadata = Object.entries(asset?.metadata ?? {}).filter(([name]) => name.endsWith('design'));
    const needsAttention = t('needs-attention');
    const status = {
        editable: props.state.editable,
        readonly: !props.state.editable,
    };
    const [picture, setPicture] = useState<DisplayPicture>({ title: '', url: '' });
    React.useEffect(() => {
        const getPictureUrl = async (picture: string) => {
            const pictureUrl = await getContentUrl(picture);
            if (!pictureUrl || pictureUrl === '') {
                dispatch(actions.event.create('error', 'Error retrieving pictures'));
            } else {
                setPicture({ title: picture, url: pictureUrl });
            }
        };
        if (asset?.picture && asset.picture !== picture.title) {
            if (asset.picture.startsWith('data:')) {
                setPicture({ title: asset.picture, url: asset.picture });
            } else {
                getPictureUrl(asset.picture);
            }
        } else {
            setPicture({ title: '', url: '' });
        }
    }, [asset?.picture]);

    if (!asset) return <span>{'#asset not in store'}</span>;

    return (
        <div className={classes.root}>
            <div className={classes.column}>
                {props.state.editable && (
                    <>
                        <span className={classes.title}>{t('title')}</span>
                        <span className={classes.subtitle}>
                            <Trans
                                i18nKey={'subtitle'}
                                components={{ choose: <span onClick={async () => uploadRef.current?.()} /> }}
                            />
                        </span>
                    </>
                )}
                <PictureUploader
                    pictures={picture.title != '' ? [picture] : []}
                    limit={1}
                    editable={props.state.editable}
                    onChange={pictures => {
                        dispatch(actions.asset.edit({ picture: pictures[0] ?? '' }));
                    }}
                    uploadRef={uploadRef}
                />
            </div>
            <div className={clsx(classes.column, classes.items, status)}>
                {props.state.editable && <span className={classes.title}>{t('design')}</span>}
                {designMetadata.map(([name, value]) => (
                    <Property
                        key={`metadata-${name}-${version}`}
                        name={name}
                        value={value}
                        error={value == undefined || value.length === 0 ? needsAttention : undefined}
                        snake
                        editable={props.state.editable}
                        onChange={(name, value) => dispatch(actions.asset.edit({ metadata: { [name]: value } }))}
                    />
                ))}
            </div>
        </div>
    );
};
